import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Button,
  Collapse,
  CircularProgress,
  Paper,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArchiveIcon from '@mui/icons-material/Archive';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import api from '../../utils/functions/api';
import * as actions from '../../redux/actions';
import ExpensesTable from '../../components/accordion/ExpensesTable';

const PAGE_SIZE = 30;
const fmt = (n) =>
  (typeof n === 'number' ? n : 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });

// ─── Shared inline stat ───────────────────────────────────────────────────────
function InlineStat({ label, value, color = 'text.secondary', tooltip }) {
  const content = (
    <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
      <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.4 }} noWrap>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color, lineHeight: 1.4 }} noWrap>
        ฿{fmt(value)}
      </Typography>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
}


// ─── Level 3: Budget accordion card (14 columns) ─────────────────────────────
function ArchiveBudgetRow({ expenses }) {
  const [open, setOpen] = useState(false);
  const budgetRows = useSelector((state) => state.budget?.rows || []);

  const firstExp = expenses[0];
  const budgetId = firstExp?.budget?._id;
  const budgetCodeLabel = firstExp?.budget?.budget_number
    ? `${firstExp.budget.prefix || ''}${firstExp.budget.budget_number}`
    : 'ไม่ระบุงบประมาณ';

  const budgetData = budgetId
    ? budgetRows.find((b) => String(b._id) === String(budgetId))
    : null;

  const bg = budgetData?.cost ?? 0;
  const paid = budgetData?.total_expenses ?? 0;
  const creditorStatus = Array.isArray(budgetData?.totalExpensesByStatus)
    ? budgetData.totalExpensesByStatus.find((s) => s.status === 'APPROVE')
    : null;
  const creditor = creditorStatus?.totalExpenses ?? 0;
  const paidCreditor = paid + creditor;
  const remain = bg > 0 ? bg - paid : 0;
  const pctRemain = bg > 0 ? (remain / bg) * 100 : 0;
  const addBg = remain;
  const pctAddBg = pctRemain;
  const isOver = remain < 0;
  const isWarning = !isOver && pctRemain < 20; // งบเหลือน้อยกว่า 20%
  const hasBudget = bg > 0;

  // สีกรอบซ้ายและ bg ตาม status — มองเห็นสถานะแม้ตอน collapse
  const statusColor = isOver ? '#dc2626' : isWarning ? '#d97706' : '#16a34a';
  const statusBg = isOver ? '#fef2f2' : isWarning ? '#fffbeb' : '#f0fdf4';
  const statusBgOpen = isOver ? '#fee2e2' : isWarning ? '#fef3c7' : '#dcfce7';
  const borderColor = isOver ? '#fca5a5' : isWarning ? '#fde68a' : '#86efac';

  const fmtPct = (n) => `${(typeof n === 'number' ? n : 0).toFixed(2)}%`;

  // สีตัวเลข remain / %เหลือ
  const remainColor = isOver ? '#dc2626' : isWarning ? '#d97706' : '#16a34a';

  return (
    <Box
      sx={{
        mb: 0.8,
        borderRadius: 1.5,
        border: `1px solid ${borderColor}`,
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      {/* Budget header */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          cursor: 'pointer',
          borderLeft: `3px solid ${statusColor}`,
          backgroundColor: open ? statusBgOpen : statusBg,
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: statusBgOpen },
        }}
      >
        <IconButton size="small" disableRipple sx={{ mr: 1, color: statusColor, flexShrink: 0 }}>
          {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>

        {/* Left: code + name */}
        <Box sx={{ flexShrink: 0, mr: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Chip
              label="งบ"
              size="small"
              sx={{
                height: 16,
                fontSize: '0.55rem',
                fontWeight: 700,
                backgroundColor: '#fde68a',
                color: '#92400e',
                borderRadius: 0.5,
                '& .MuiChip-label': { px: 0.6 },
              }}
            />
            <Typography fontWeight={700} fontSize="0.85rem" color="#92400e" noWrap>
              {budgetCodeLabel}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap>
            {budgetData?.name || '-'} · {expenses.length} รายการ
          </Typography>
        </Box>

        {/* Right: 14-column stats */}
        {hasBudget && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              overflowX: 'auto',
              '& > *:not(:last-child)': { borderRight: '1px dashed #e5e7eb' },
            }}
          >
            {/* BG — reference, blue */}
            <InlineStat label="BG" value={bg} color="#1d4ed8" tooltip="งบประมาณที่ตั้ง" />
            {/* จ่ายแล้ว — informational, muted */}
            <InlineStat label="จ่ายแล้ว" value={paid} color="#374151" tooltip="ค่าใช้จ่ายที่จ่ายแล้ว (SUCCESS)" />
            {/* จ่ายแล้ว+เจ้าหนี้ — amber */}
            <InlineStat label="จ่าย+เจ้าหนี้" value={paidCreditor} color="#b45309" tooltip="จ่ายแล้ว + เจ้าหนี้" />
            {/* %เหลือ — status color */}
            <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>%เหลือ</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: remainColor, lineHeight: 1.3 }} noWrap>
                {fmtPct(pctRemain)}
              </Typography>
            </Box>
            {/* เหลือสุทธิ — KEY indicator, status color + slightly larger */}
            <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>เหลือสุทธิ</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: remainColor, lineHeight: 1.3 }} noWrap>
                ฿{fmt(remain)}
              </Typography>
            </Box>
            {/* คาดว่าใช้อีก — muted */}
            <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>คาดว่าใช้อีก</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'text.disabled', lineHeight: 1.3 }} noWrap>฿0.00</Typography>
            </Box>
            {/* เติม BG — muted ถ้าปกติ, แดงถ้าเกิน */}
            <InlineStat label="เติม BG" value={addBg} color={isOver ? '#dc2626' : 'text.disabled'} tooltip="งบที่ต้องเติม" />
            {/* %เติม BG */}
            <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>%เติม BG</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: isOver ? '#dc2626' : 'text.disabled', lineHeight: 1.3 }} noWrap>
                {fmtPct(pctAddBg)}
              </Typography>
            </Box>
            {/* ต้องใช้อีก — muted */}
            <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>ต้องใช้อีก</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'text.disabled', lineHeight: 1.3 }} noWrap>0</Typography>
            </Box>
            {/* เจ้าหนี้ — amber ถ้ามีค่า, muted ถ้าเป็น 0 */}
            <InlineStat label="เจ้าหนี้" value={creditor} color={creditor > 0 ? '#b45309' : 'text.disabled'} tooltip="เจ้าหนี้ (APPROVE ยังไม่จ่าย)" />
            {/* อัพเดต — muted */}
            <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>อัพเดต</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'text.disabled', lineHeight: 1.3 }} noWrap>
                {budgetData?.updatedAt ? dayjs(budgetData.updatedAt).format('DD/MM/YY') : '-'}
              </Typography>
            </Box>
            {/* หมายเหตุ — muted */}
            <Tooltip title={budgetData?.description || ''}>
              <Box sx={{ px: 2, py: 0.4, textAlign: 'center', flexShrink: 0, maxWidth: 140 }}>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>หมายเหตุ</Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'text.disabled', lineHeight: 1.3 }} noWrap>
                  {budgetData?.description || '-'}
                </Typography>
              </Box>
            </Tooltip>
            {/* Status badge */}
            <Box sx={{ px: 1, flexShrink: 0 }}>
              {isOver ? (
                <Chip label="เกินงบ!" size="small"
                  sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }} />
              ) : isWarning ? (
                <Chip label="ใกล้หมด" size="small"
                  sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, backgroundColor: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }} />
              ) : (
                <Chip label="ปกติ" size="small"
                  sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac' }} />
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Expand: Expenses table */}
      <Collapse in={open}>
        <Divider />
        <Box sx={{ backgroundColor: '#fff', maxHeight: 480, overflowY: 'auto' }}>
          <ExpensesTable expenses={expenses} />
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Level 2: Project accordion card ─────────────────────────────────────────
function ArchiveProjectRow({ expenses }) {
  const [open, setOpen] = useState(false);
  const budgetRows = useSelector((state) => state.budget?.rows || []);
  const projectRows = useSelector((state) => state.project?.rows || []);

  const firstExp = expenses[0];
  const projectName = firstExp?.project?.name || 'ไม่ระบุโครงการ';
  const projectNumber = firstExp?.project?.project_number || '';
  const projectId = String(firstExp?.project?._id || firstExp?.project?.id || '');

  const projectData = projectRows.find(
    (p) => String(p._id) === projectId || String(p.id) === projectId,
  );
  const contractValue = projectData?.cost ?? 0;
  const laborCost = projectData?.labur_cost ?? 0;
  const vat = projectData?.vat ?? 0;

  // ─── Project status fields ────────────────────────────────────────────────
  const opStatus = projectData?.operation_status || 'PENDING';
  const deliverDone = !!projectData?.deliver_status?.status;
  const poDone = !!projectData?.po_status;
  const boqDone = !!projectData?.boq_status;
  const accDone = !!projectData?.acc_status?.status;
  const paymentDone = !!projectData?.payment_status?.status;

  const OP_STATUS_MAP = {
    PENDING: { label: 'รอดำเนินการ', color: '#6b7280', bg: '#f3f4f6' },
    IN_PROGRESS: { label: 'ดำเนินการอยู่', color: '#2563eb', bg: '#dbeafe' },
    DELIVERY: { label: 'ส่งมอบงาน', color: '#d97706', bg: '#fef3c7' },
    BILLING: { label: 'วางบิลแล้ว', color: '#7c3aed', bg: '#ede9fe' },
    COMPLETE: { label: 'เสร็จงาน', color: '#15803d', bg: '#dcfce7' },
  };
  const opCfg = OP_STATUS_MAP[opStatus] || { label: opStatus, color: '#6b7280', bg: '#f3f4f6' };

  const statusChip = (label, done, trueLabel, falseLabel) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
      <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', mb: 0.2 }} noWrap>{label}</Typography>
      <Chip
        label={done ? trueLabel : falseLabel}
        size="small"
        sx={{
          height: 16,
          fontSize: '0.55rem',
          fontWeight: 700,
          backgroundColor: done ? '#dcfce7' : '#f3f4f6',
          color: done ? '#15803d' : '#6b7280',
          '& .MuiChip-label': { px: 0.6 },
        }}
      />
    </Box>
  );

  const budgetIds = [
    ...new Set(expenses.map((e) => e.budget?._id).filter(Boolean).map(String)),
  ];
  const projectBudgets = budgetIds
    .map((id) => budgetRows.find((b) => String(b._id) === id))
    .filter(Boolean);

  const totalBudget = _.sumBy(projectBudgets, 'cost') || 0;

  const getStatusAmt = (b, statusKey) => {
    if (Array.isArray(b?.totalStatus)) {
      return b.totalStatus.find((s) => s.status === statusKey)?.totalExpenses ?? 0;
    }
    return b?.totalExpensesByStatus?.[statusKey] ?? 0;
  };
  const totalSuccess = _.sumBy(projectBudgets, (b) => getStatusAmt(b, 'SUCCESS'));
  const totalApprove = _.sumBy(projectBudgets, (b) => getStatusAmt(b, 'APPROVE'));
  const remaining = totalBudget - totalSuccess - totalApprove;
  const isOver = remaining < 0;
  const hasBudget = totalBudget > 0;

  // Group expenses by budget
  const budgetGroups = _.groupBy(
    expenses,
    (e) => e.budget?._id || e.budget?.budget_number || '__none__',
  );
  const budgetKeys = Object.keys(budgetGroups);

  return (
    <Box
      sx={{
        mb: 1,
        borderRadius: 1.5,
        border: '1px solid #ede9fe',
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      {/* Project header */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          px: 2,
          py: 1.2,
          cursor: 'pointer',
          borderLeft: open ? '3px solid #8b5cf6' : '3px solid transparent',
          backgroundColor: open ? '#faf5ff' : 'transparent',
          transition: 'background-color 0.15s, border-left-color 0.15s',
          '&:hover': { backgroundColor: open ? '#faf5ff' : '#fdf4ff' },
        }}
      >
        <IconButton
          size="small"
          sx={{ mr: 1, mt: 0.3, color: open ? '#8b5cf6' : 'text.secondary', flexShrink: 0 }}
          disableRipple
        >
          {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>

        {/* Left: project info + status chips */}
        <Box sx={{ flexShrink: 0, mr: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Chip
              label="โครงการ"
              size="small"
              sx={{
                height: 16,
                fontSize: '0.55rem',
                fontWeight: 700,
                backgroundColor: '#ede9fe',
                color: '#7c3aed',
                borderRadius: 0.5,
                '& .MuiChip-label': { px: 0.6 },
              }}
            />
            <Typography fontWeight={600} fontSize="0.85rem" noWrap>
              {projectName}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {projectNumber && (
              <Typography component="span" variant="caption" fontWeight={700} color="#7c3aed" sx={{ mr: 0.5 }}>
                {projectNumber}
              </Typography>
            )}
            {expenses.length} รายการ · {budgetKeys.length} งบประมาณ
          </Typography>

          {/* Status chips row */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.6, flexWrap: 'wrap' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* สถานะงาน */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', mb: 0.2 }} noWrap>สถานะงาน</Typography>
              <Chip
                label={opCfg.label}
                size="small"
                sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, backgroundColor: opCfg.bg, color: opCfg.color, '& .MuiChip-label': { px: 0.6 } }}
              />
            </Box>
            {statusChip('สถานะส่งงาน', deliverDone, 'ส่งแล้ว', 'ยังไม่ส่ง')}
            {statusChip('การเบิกจ่าย (PO)', poDone, 'มี PO', 'ไม่มี PO')}
            {statusChip('Warranty', boqDone, 'มี', 'ไม่มีข้อมูล')}
            {statusChip('สถานะวางบิล', accDone, 'วางบิลแล้ว', 'ยังไม่วางบิล')}
            {statusChip('สถานะรับเงิน', paymentDone, 'รับเงินแล้ว', 'ยังไม่รับเงิน')}
          </Box>
        </Box>

        {/* Right: budget stats */}
        {hasBudget && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexGrow: 1,
              pt: 0.3,
              '& > *:not(:last-child)': { borderRight: '1px dashed #ede9fe' },
            }}
          >
            {contractValue > 0 && (
              <InlineStat label="มูลค่าสัญญา" value={contractValue} color="#7c3aed" tooltip="มูลค่าสัญญาโครงการ" />
            )}
            <InlineStat label="งบรวม" value={totalBudget} color="#1d4ed8" tooltip="งบประมาณรวมทุกก้อน" />
            <InlineStat label="จ่ายแล้ว" value={totalSuccess} color="#15803d" tooltip="ค่าใช้จ่ายที่จ่ายแล้ว (SUCCESS)" />
            <InlineStat label="เจ้าหนี้" value={totalApprove} color="#b45309" tooltip="อนุมัติแล้ว ยังไม่จ่าย (APPROVE)" />
            <InlineStat
              label="คงเหลือ"
              value={remaining}
              color={isOver ? '#dc2626' : '#15803d'}
              tooltip={isOver ? 'เกินงบ!' : 'งบคงเหลือ'}
            />
            {laborCost > 0 && (
              <InlineStat label="ค่าแรง" value={laborCost} color="text.secondary" tooltip="ค่าแรงงาน" />
            )}
            {vat > 0 && (
              <InlineStat label="VAT" value={vat} color="text.secondary" tooltip="VAT โครงการ" />
            )}
            {isOver && (
              <Box sx={{ px: 1 }}>
                <Chip
                  label="เกินงบ!"
                  size="small"
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    height: 18,
                    backgroundColor: '#fef2f2',
                    color: '#dc2626',
                    border: '1px solid #fca5a5',
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        <Typography fontWeight={600} fontSize="0.85rem" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
          {fmt(_.sumBy(expenses, 'total'))} ฿
        </Typography>
      </Box>

      {/* Budget accordion cards inside project */}
      <Collapse in={open}>
        <Box sx={{ borderTop: '1px solid #ede9fe', backgroundColor: '#fdf8ff', px: 1.5, py: 1 }}>
          {budgetKeys.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1.5 }}>
              ไม่มีรายการ
            </Typography>
          ) : (
            budgetKeys.map((budgetKey) => (
              <ArchiveBudgetRow
                key={budgetKey}
                expenses={budgetGroups[budgetKey]}
              />
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Level 1: Customer accordion ─────────────────────────────────────────────
function ArchiveCustomerRow({ customerName, projectGroups }) {
  const [open, setOpen] = useState(false);

  const projectKeys = Object.keys(projectGroups);
  const totalExpenseCount = useMemo(
    () => Object.values(projectGroups).reduce((sum, arr) => sum + arr.length, 0),
    [projectGroups],
  );

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2,
        border: '1px solid rgb(var(--color-primary-200))',
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: open
          ? '0 2px 8px rgb(var(--color-primary-500) / 0.10)'
          : '0 1px 3px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.2s',
      }}
    >
      {/* Customer header */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          borderLeft: open ? '4px solid rgb(var(--color-primary-500))' : '4px solid transparent',
          backgroundColor: open ? 'rgb(var(--color-primary-50))' : '#f5f3ff',
          transition: 'background-color 0.15s, border-left-color 0.15s',
          '&:hover': { backgroundColor: 'rgb(var(--color-primary-50))' },
        }}
      >
        <IconButton
          size="small"
          sx={{ mr: 1, color: open ? 'rgb(var(--color-primary-500))' : 'text.secondary' }}
          disableRipple
        >
          {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label="ลูกค้า"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 700,
                backgroundColor: 'rgb(var(--color-primary-100))',
                color: 'rgb(var(--color-primary-700))',
                borderRadius: 1,
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
            <Typography fontWeight={700} fontSize="1rem" color="rgb(var(--color-primary-700))" noWrap>
              {customerName}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {projectKeys.length} โครงการ · {totalExpenseCount} รายการค่าใช้จ่าย
          </Typography>
        </Box>
      </Box>

      {/* Projects inside customer */}
      <Collapse in={open}>
        <Box
          sx={{
            borderTop: '2px solid rgb(var(--color-primary-200))',
            backgroundColor: '#f0f2ff',
            px: 1.5,
            py: 1.5,
          }}
        >
          {projectKeys.map((projKey) => (
            <ArchiveProjectRow
              key={projKey}
              expenses={projectGroups[projKey]}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────
export default function FinanceArchive({ title }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const projectRows = useSelector((state) => state.project?.rows || []);

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(null);

  const [filterSearch, setFilterSearch] = useState('');

  const loadPage = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/payment_api?status=SUCCESS&size=${PAGE_SIZE}&page=${p}`,
      );
      const rawRows = data?.rows || [];
      const todayKey = dayjs().format('YYYY-MM-DD');
      const newRows = rawRows.filter((pmt) => {
        const pDateKey = dayjs(pmt.datePayment || pmt.dateRequest || pmt.date).format('YYYY-MM-DD');
        return pDateKey < todayKey;
      });
      setTotal(data?.total ?? null);
      setRows((prev) => (p === 1 ? newRows : [...prev, ...newRows]));
      setHasMore(rawRows.length === PAGE_SIZE);
      // ExpensesTable (ตารางรายการในใบจ่าย) เติมข้อมูลเต็มจาก state.expenses — เดิมดึงรายการเบิกทั้งระบบ
      // (size 9999) ซึ่งเกินขีดจำกัด 16MB แล้ว (500) ตอนนี้ดึงเฉพาะรายการที่อยู่ในใบจ่ายหน้านี้เท่านั้น
      dispatch(
        actions.expensesMergeByIds(
          newRows.flatMap((pmt) => (pmt.expenses_list || []).map((e) => e.expenses_id)),
        ),
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1);
    dispatch(actions.projectAll({ size: 9999, page: 1, lite: true }));
    dispatch(actions.budgetAll({}));
  }, []);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    loadPage(next);
  };

  // เฉพาะ expenses จาก SUCCESS payments เท่านั้น — รายการที่ยังไม่จ่ายอยู่ในหน้า Summary
  const allArchiveExpenses = useMemo(() => {
    return rows.flatMap((p) =>
      (p.expenses_list || []).map((e) => ({ ...e, _payment: p })),);
  }, [rows]);

  // Group: customer → project key → [expenses]
  const byCustomer = useMemo(() => {
    const groups = {};
    allArchiveExpenses.forEach((exp) => {
      const projectId = String(exp.project?._id || exp.project?.id || '');
      const projectData = projectRows.find((p) => String(p._id) === projectId || String(p.id) === projectId);
      const customer =
        projectData?.customer || exp.project?.customer || 'ไม่ระบุลูกค้า';

      if (!groups[customer]) groups[customer] = {};
      const projKey = projectId || exp.project?.name || '__none__';
      if (!groups[customer][projKey]) groups[customer][projKey] = [];
      groups[customer][projKey].push(exp);
    });
    return groups;
  }, [allArchiveExpenses, projectRows]);

  // Filter by search text
  const filteredCustomers = useMemo(() => {
    return Object.entries(byCustomer)
      .filter(([customerName, projectGroups]) => {
        if (!filterSearch) return true;
        const q = filterSearch.toLowerCase();
        if (customerName.toLowerCase().includes(q)) return true;
        return Object.values(projectGroups).some((expenses) => {
          const firstExp = expenses[0];
          if (firstExp?.project?.name?.toLowerCase().includes(q)) return true;
          if (firstExp?.project?.project_number?.toLowerCase().includes(q)) return true;
          return expenses.some(
            (e) =>
              e.expenses_code?.toLowerCase().includes(q) ||
              String(e.expenses_id).includes(q),
          );
        });
      })
      .sort((a, b) => a[0].localeCompare(b[0], 'th'));
  }, [byCustomer, filterSearch]);

  const isLoading = loading;
  const totalExpenses = allArchiveExpenses.length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton size="small" onClick={() => history.push('/finance/summary')}>
          <ArrowBackIcon />
        </IconButton>
        <ArchiveIcon sx={{ color: '#64748b' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {title || 'Archive'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filteredCustomers.length} ลูกค้า
            {total !== null ? ` · ${total} payments` : ''}
            {totalExpenses > 0 ? ` · ${totalExpenses} รายการค่าใช้จ่าย` : ''}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Filter bar */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2.5, borderRadius: 2 }}>
        <TextField
          placeholder="ค้นหา ลูกค้า โครงการ รหัสเอกสาร..."
          size="small"
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          sx={{ minWidth: 260, maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: filterSearch ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setFilterSearch('')}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Paper>

      {/* Loading */}
      {isLoading && allArchiveExpenses.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No data */}
      {!isLoading && filteredCustomers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" variant="body2">
            ไม่มีรายการ
          </Typography>
        </Box>
      )}

      {/* Customer rows */}
      {filteredCustomers.map(([customerName, projectGroups]) => (
        <ArchiveCustomerRow
          key={customerName}
          customerName={customerName}
          projectGroups={projectGroups}
        />
      ))}

      {/* Load more payments */}
      {!loading && hasMore && rows.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 3 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleLoadMore}
            sx={{ borderRadius: 1.5 }}
          >
            โหลดเพิ่มเติม (ต่อไป {PAGE_SIZE} payments)
          </Button>
        </Box>
      )}
      {loading && rows.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {!loading && !hasMore && rows.length > 0 && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: 'block', textAlign: 'center', mt: 1, mb: 3 }}
        >
          แสดงครบทุก payment แล้ว ({rows.length} payments)
        </Typography>
      )}
    </Box>
  );
}
